import { XSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { TWITTER_AUTOMATION_STAGE_STATUSES } from './twitterAutomationStageStatuses'
import { NO_TIMEOUT } from '../../consts/stageMachineConsts'
import { xRateLimitFaqUrl } from '../../consts/urlConsts'
import { sendAlertNotification } from '../../utils/communication'
import {
  PLATFORM,
  StageType,
  TwitterRecommendationKey,
  TriggeringEventType,
  ControlStageKey,
} from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getStoredMachineOverlayStatus,
  setStoredMachineOverlayStatus,
} from '../automationStageMachine'
import {
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import { evaluateXpath, getCaseInsensitiveString } from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { getInitialValueBySettingKey } from '../utils'

const getCurrentValueFn = () => () => ''
const getIsFulfilledFn = () => () => true
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

async function getPrimaryColumn() {
  const timeline = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    () =>
      evaluateXpath<HTMLElement>(
        document,
        '//div[@data-testid="primaryColumn"]',
      )[0],
  )

  return timeline
}

type PostInfo = {
  authorName: string
  authorUsername: string
  content: string
  repostCount: string
  urlId: string
}

async function getCurrentPostInfo(): Promise<PostInfo> {
  let postUrlId
  let authorUsername

  if (window?.location?.href) {
    const currentUrl = new URL(window.location.href)
    const pattern = /\/(\w*)\/status\/(\d*)/gm
    const postUrlIdMatch = pattern.exec(currentUrl.pathname)

    if (postUrlIdMatch && postUrlIdMatch.length > 1) {
      authorUsername = postUrlIdMatch[1]
      postUrlId = postUrlIdMatch[2]
    }
  }

  if (authorUsername && postUrlId) {
    const currentPost = await getWaitForElementToAppearPromise(
      document,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          `//article[descendant::span[${getCaseInsensitiveString('text()')}="@${authorUsername.toLowerCase()}"]]`,
        )[0],
      3000,
    )

    if (!currentPost) {
      return null
    }

    const authorElement = await getWaitForElementToAppearPromise(
      currentPost,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          'descendant::div[@data-testid="User-Name"]',
        )[0],
      3000,
    )

    const authorText = authorElement?.textContent
    const authorNameArr = authorText ? authorText.split('@') : []

    const content = await getWaitForElementToAppearPromise(
      currentPost,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          'descendant::div[@data-testid="tweetText"]',
        )[0],
      3000,
    )

    const reposts = await getWaitForElementToAppearPromise(
      currentPost,
      (node) =>
        evaluateXpath<HTMLElement>(
          node,
          'descendant::button[contains(@aria-label, "Repost")]',
        )[0],
      3000,
    )

    // author name is everything before the last @
    const authorName =
      authorNameArr && authorNameArr.length > 0
        ? authorNameArr.slice(0, -1).join('')
        : ''

    return {
      authorName,
      authorUsername,
      content: content?.textContent,
      repostCount: reposts?.textContent,
      urlId: postUrlId,
    }
  }

  return null
}

function getUsernameFromRow(row: HTMLElement): string {
  const userLink = evaluateXpath<HTMLAnchorElement>(row, 'descendant::a')[0]

  const href = userLink?.href

  if (href) {
    const pattern = /https:\/\/x.com\/(.+)/gm
    const usernameMatch = pattern.exec(href)

    if (usernameMatch && usernameMatch.length >= 1) {
      return usernameMatch[1]
    }
  }

  return null
}

async function clickProfile(accountName: string): Promise<boolean> {
  const profileLink = await getWaitForElementToAppearPromise<HTMLAnchorElement>(
    document,
    (node) =>
      evaluateXpath<HTMLAnchorElement>(node, `//a[@href="/${accountName}"]`)[0],
    5000,
  )

  if (profileLink) {
    profileLink.click()
    return true
  }
  return false
}

async function gatherVisibleReposters(): Promise<string[]> {
  const pageContent = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(node, '//*[@data-testid="primaryColumn"]')[0],
    5000,
  )

  if (pageContent) {
    const userCells = evaluateXpath<HTMLDivElement>(
      pageContent,
      'descendant::button[@data-testid="UserCell"]',
    )

    return userCells.map((x) => getUsernameFromRow(x)).filter((x) => !!x)
  }

  return []
}

async function blockProfile(isDry: boolean): Promise<boolean> {
  const userActionMenu = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        '//*[@aria-label="Home timeline"]//button[@aria-label="More" and @data-testid="userActions"]',
      )[0],
    3000,
  )

  if (userActionMenu) {
    userActionMenu.click()
  }

  const blockItem = await getWaitForElementToAppearPromise<HTMLElement>(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        '//div[@role="menuitem" and @data-testid="block"]',
      )[0],
    2000,
  )

  if (blockItem) {
    blockItem.click()
  }

  await wait(200)

  const confirmBlockButton =
    await getWaitForElementToAppearPromise<HTMLButtonElement>(
      document,
      (node) =>
        evaluateXpath<HTMLButtonElement>(
          node,
          '//button[@role="button"]//*[text()="Block"]',
        )[0],
      3000,
    )

  const cancelButton =
    await getWaitForElementToAppearPromise<HTMLButtonElement>(
      document,
      (node) =>
        evaluateXpath<HTMLButtonElement>(
          node,
          '//button[@role="button"]//*[contains(text(), "Cancel")]',
        )[0],
      3000,
    )

  if (confirmBlockButton && !isDry) {
    confirmBlockButton.click()
    return true
  }

  cancelButton?.click()
  return false
}

async function updateStageStatusWithCurrenBlockInfo(
  currentUsername: string,
  nTotalAccounts: number,
): Promise<void> {
  const currentStatus = await getStoredMachineOverlayStatus(PLATFORM.TWITTER)

  await setStoredMachineOverlayStatus(PLATFORM.TWITTER, {
    ...currentStatus,
    currentUsername,
    nTotalAccounts,
  })
}

const StageKey = {
  GATHER_NAVIGATE_TO_POST: 'GATHER_NAVIGATE_TO_POST',
  GATHER_POST: 'GATHER_POST',
  UPDATE_BLOCK_POST_AUTHOR: 'UPDATE_BLOCK_POST_AUTHOR',
  UPDATE_BLOCK_ALL_RETWEETS: 'UPDATE_BLOCK_ALL_RETWEETS',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_NAVIGATE_TO_POST,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    transitionNextStageMap: {
      [TriggeringEventType.ON_ERROR]: ControlStageKey.CANCEL,
    },
    action: async (machineContext, userContext, additionalMachineData) => {
      const { updateStoredAutomationErrors } = userContext
      const { inputData } = additionalMachineData

      const inputPostUrl =
        inputData[TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS]?.postUrl

      if (inputPostUrl) {
        if (inputPostUrl !== window?.location?.href) {
          window.location.assign(inputPostUrl)
        }
      } else {
        await updateStoredAutomationErrors(
          {
            [XSettingKey.BLOCK_REPOSTERS]: {
              message: 'No post detected',
            },
          },
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
        )
        throw new Error('No post url saved')
      }
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_POST,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    transitionNextStageMap: {
      [TriggeringEventType.ON_ERROR]: ControlStageKey.CANCEL,
    },
    action: async (machineContext, userContext) => {
      const {
        initialValues: storedInitialValues,
        clearStoredAutomationErrors,
        updateStoredAutomationErrors,
        updateStoredInitialValues,
      } = userContext

      const initialValues =
        getInitialValueBySettingKey(
          storedInitialValues,
          XSettingKey.BLOCK_REPOSTERS,
        ) || {}

      await clearStoredAutomationErrors(
        TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
      )

      let currentPost
      try {
        currentPost = await getCurrentPostInfo()
      } finally {
        await updateStoredInitialValues(
          {
            [XSettingKey.BLOCK_REPOSTERS]: {
              ...initialValues,
              postInfo: currentPost,
            },
          },
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
        )

        if (!currentPost) {
          await updateStoredAutomationErrors(
            {
              [XSettingKey.BLOCK_REPOSTERS]: {
                message: 'No post detected',
              },
            },
            TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
          )
        }
      }
    },
  },
]

async function getReposterList(): Promise<string[]> {
  const seenReposters = new Set<string>()
  let prevNumberReposters = 0
  let numTryNoChange = 0
  let scrolled = false

  while (numTryNoChange < 3 && !scrolled) {
    window.scrollBy(0, 3000)
    scrolled = true
    await wait(1000)

    const visibleReposters = await gatherVisibleReposters()
    visibleReposters.forEach((reposter) => seenReposters.add(reposter))

    if (seenReposters.size > prevNumberReposters) {
      prevNumberReposters = seenReposters.size
    } else if (seenReposters.size === prevNumberReposters) {
      numTryNoChange += 1
    }
  }

  remoteLog.debug(
    `getRepostersList: finished scrolling. can see ${seenReposters.size} total reposters`,
  )
  return [...seenReposters]
}

type repostersProgress = {
  blocked: string[]
  toBlock: string[]
}

async function refreshReposters(resumeData): Promise<repostersProgress> {
  const respostersToBlock = await getReposterList()
  const newResumeData = {
    repostersToBlock: [...respostersToBlock],
  }
  await resumeData.set({ ...resumeData.data, ...newResumeData })

  const blockedReposters = new Set<string>(
    resumeData.data.blockedReposters || [],
  )
  const rePostersNotYetBlocked = respostersToBlock.filter(
    (x) => !blockedReposters.has(x),
  )

  remoteLog.debug(
    `refreshReposters: ${rePostersNotYetBlocked.length} reposters to block, ` +
      `${blockedReposters.size} already blocked`,
  )
  return { blocked: [...blockedReposters], toBlock: rePostersNotYetBlocked }
}

function hasNoPostInfo(initialValues: { [key: string]: any }) {
  return (
    !initialValues?.postInfo ||
    !initialValues?.postInfo?.authorUsername ||
    !initialValues?.postInfo?.urlId
  )
}

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_BLOCK_POST_AUTHOR,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.BLOCKING_REPOSTERS,
    action: async (machineContext, userContext) => {
      const {
        initialValues: storedInitialValues,
        isDry,
        updateStoredInitialValues,
      } = userContext

      const initialValues =
        getInitialValueBySettingKey(
          storedInitialValues,
          XSettingKey.BLOCK_REPOSTERS,
        ) || {}

      // Exit if no post info in initial values
      if (hasNoPostInfo(initialValues)) {
        return
      }

      const username = initialValues?.postInfo?.authorUsername

      const authorProfileUrl = `https://x.com/${username}/`
      if (window.location.href !== authorProfileUrl) {
        window.location.assign(authorProfileUrl)
      }

      await updateStageStatusWithCurrenBlockInfo(`@${username}`, 0)
      const blocked = await blockProfile(isDry)

      if (blocked) {
        const initialCount = initialValues?.count || 0

        await updateStoredInitialValues(
          {
            [XSettingKey.BLOCK_REPOSTERS]: {
              ...initialValues,
              count: initialCount + 1,
              message:
                initialCount + 1 === 1
                  ? 'Blocked 1 account (all time total).'
                  : `Blocked ${initialCount + 1} accounts (all time total).`,
            },
          },
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
        )
      }
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_BLOCK_ALL_RETWEETS,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.BLOCKING_REPOSTERS,
    timeout: NO_TIMEOUT,
    action: async (machineContext, userContext, additionalMachineData) => {
      const {
        clearStoredAutomationErrors,
        incrementTotalCount,
        initialValues: storedInitialValues,
        isDry,
        updateStoredAutomationErrors,
        updateStoredInitialValues,
      } = userContext
      const { resumeData } = additionalMachineData

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        XSettingKey.BLOCK_REPOSTERS,
      )

      const updatedInitialValues = {
        ...initialValues,
        result: null,
      }

      // Exit if no post info in initial values
      if (hasNoPostInfo(initialValues)) {
        return
      }

      await clearStoredAutomationErrors(
        TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
      )
      await updateStoredInitialValues(
        {
          [XSettingKey.BLOCK_REPOSTERS]: updatedInitialValues,
        },
        TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
      )

      const postUrlId = initialValues?.postInfo?.urlId
      const username = initialValues?.postInfo?.authorUsername
      const postUrl = `https://x.com/${username}/status/${postUrlId}/retweets`
      if (window.location.href !== postUrl) {
        window.location.assign(postUrl)
      }

      await wait(500)
      const content = await getPrimaryColumn()
      if (!content) {
        await updateStoredAutomationErrors(
          {
            [XSettingKey.BLOCK_REPOSTERS]: {
              message:
                'Unable to load post engagement, failed to block reposters.',
            },
          },
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
        )
        throw Error(
          'Unable to load post engagement, failed to block reposters.',
        )
      }

      const { blocked, toBlock } = await refreshReposters(resumeData)

      // Initial count is 1 for the post author (no page navigation/reload from last stage)
      const initialCount = initialValues?.count || 1
      let currentCount = blocked.length
      let currentToBlockList = toBlock
      const blockedReposters = blocked

      if (currentToBlockList && currentToBlockList.length > 0) {
        while (currentToBlockList.length > 0) {
          for (const accountName of currentToBlockList) {
            await updateStageStatusWithCurrenBlockInfo(
              `@${accountName}`,
              currentCount,
            )
            const clickedProfile = await clickProfile(accountName)

            if (clickedProfile) {
              const isBlockSuccess = await blockProfile(isDry)
              await wait(1000)

              if (isBlockSuccess) {
                currentCount += 1
                updatedInitialValues.count = currentCount + initialCount
                updatedInitialValues.message =
                  currentCount + initialCount === 1
                    ? 'Blocked 1 account (all time total).'
                    : `Blocked ${currentCount + initialCount} accounts (all time total).`

                await updateStoredInitialValues(
                  {
                    [XSettingKey.BLOCK_REPOSTERS]: updatedInitialValues,
                  },
                  TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
                )

                blockedReposters.push(accountName)
                await resumeData.set({
                  ...resumeData.data,
                  repostersToBlock: [...currentToBlockList],
                  blockedReposters: [...blockedReposters],
                })
              }

              window.history.back()
            }
          }

          const newResumeData = {
            set: resumeData.set,
            data: {
              repostersToBlock: [...currentToBlockList],
              blockedReposters: [...blockedReposters],
            },
          }
          await wait(500)
          const { toBlock: newBlockList } =
            await refreshReposters(newResumeData)
          currentToBlockList = newBlockList
        }

        const title =
          `You blocked @${username} ` +
          `and ${currentCount} reposters of their post`
        let detail
        let helper = {
          text: 'Did we miss someone? ',
          link: xRateLimitFaqUrl,
        }

        if (currentCount.toString() !== initialValues.postInfo.repostCount) {
          detail =
            'We try our best to block as many users as possible in one pass, ' +
            'but there may still be more to go. Block more reposters by trying ' +
            'again or coming back later.'

          helper = {
            text: "Why wasn't everyone blocked? ",
            link: xRateLimitFaqUrl,
          }
        }

        await incrementTotalCount(
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
          // +1 for post author
          currentCount + 1,
        )

        await userContext.updateStatisticsLastCount(
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
          currentCount + 1,
        )

        updatedInitialValues.result = {
          title,
          detail,
          helper,
        }

        await updateStoredInitialValues(
          {
            [XSettingKey.BLOCK_REPOSTERS]: updatedInitialValues,
          },
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
        )
      } else {
        // reposters to block was empty
        await incrementTotalCount(
          // Only post author was blocked
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
        )

        updatedInitialValues.result = {
          title: "You're good to go",
          detail: 'There are no more reposters to block.',
          helper: {
            text: "Why wasn't everyone blocked? ",
            link: xRateLimitFaqUrl,
          },
        }

        await updateStoredInitialValues(
          {
            [XSettingKey.BLOCK_REPOSTERS]: updatedInitialValues,
          },
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
        )

        await userContext.updateStatisticsLastCount(
          TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS,
          1,
        )
      }

      sendAlertNotification(
        PLATFORM.TWITTER,
        'X: Block reposters finished',
        `Blocked @${username} and ${currentCount} reposters of their post.`,
        postUrl,
      )
    },
  },
]
const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
